import * as angular from "angular";

const TAB_SELECTOR = "[role=tab]";

interface TabListNavigationAttributes extends angular.IAttributes {
	tabListItems?: string;
	tabListSelected?: string;
}

class TabListNavigationDirective implements angular.IDirective {
	public restrict = "A";

	public link(
		scope: angular.IScope,
		element: angular.IAugmentedJQuery,
		attributes: TabListNavigationAttributes
	): void {
		const tabList = element[0];

		const getTabs = (): HTMLElement[] => Array
			.from(tabList.querySelectorAll<HTMLElement>(TAB_SELECTOR));
		const getSelectableTabs = (): HTMLElement[] => getTabs()
			.filter(tab => tab.getAttribute("aria-disabled") !== "true");
		const setFocusableTab = (focusableTab?: HTMLElement): void => {
			getTabs().forEach(tab => tab.setAttribute("tabindex", tab === focusableTab ? "0" : "-1"));
		};
		const syncFocusableTab = (): void => {
			const selectableTabs = getSelectableTabs();
			const selectedTab = selectableTabs.find(tab => tab.getAttribute("aria-selected") === "true");
			const currentTab = selectableTabs.find(tab => tab.getAttribute("tabindex") === "0");
			setFocusableTab(selectedTab ?? currentTab ?? selectableTabs[0]);
		};
		const scheduleSync = (): void => {
			scope.$evalAsync(syncFocusableTab);
		};
		const moveFocus = (tab: HTMLElement): void => {
			setFocusableTab(tab);
			tab.focus();
		};
		const handleKeydown = (event: any): void => {
			const keyboardEvent = event as unknown as KeyboardEvent;
			const currentTab = keyboardEvent.target as HTMLElement;
			if(currentTab.getAttribute("role") !== "tab") {
				return;
			}

			if(keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
				keyboardEvent.preventDefault();
				currentTab.click();
				return;
			}

			const tabs = getSelectableTabs();
			const currentIndex = tabs.indexOf(currentTab);
			if(currentIndex < 0) {
				return;
			}

			const isVertical = tabList.getAttribute("aria-orientation") === "vertical";
			let nextTab: HTMLElement | undefined;
			switch(keyboardEvent.key) {
				case isVertical ? "ArrowDown" : "ArrowRight":
					nextTab = tabs[(currentIndex + 1) % tabs.length];
					break;
				case isVertical ? "ArrowUp" : "ArrowLeft":
					nextTab = tabs[(currentIndex - 1 + tabs.length) % tabs.length];
					break;
				case "Home":
					nextTab = tabs[0];
					break;
				case "End":
					nextTab = tabs[tabs.length - 1];
					break;
				default:
					return;
			}

			if(nextTab) {
				keyboardEvent.preventDefault();
				moveFocus(nextTab);
			}
		};

		tabList.setAttribute("role", "tablist");
		element.on("keydown", handleKeydown);

		const unwatchItems = attributes.tabListItems
			? scope.$watchCollection(() => scope.$eval(attributes.tabListItems as string), scheduleSync)
			: angular.noop;
		const unwatchSelected = attributes.tabListSelected
			? scope.$watch(() => scope.$eval(attributes.tabListSelected as string), scheduleSync)
			: angular.noop;

		scheduleSync();
		scope.$on("$destroy", () => {
			element.off("keydown", handleKeydown);
			unwatchItems();
			unwatchSelected();
		});
	}
}

export default angular
	.module("vesl.behaviors.tab-list-navigation", [])
	.directive("tabListNavigation", () => new TabListNavigationDirective())
	.name;
